/**
 * The SSN block of a split job (CAL-5987) - pure. After Save the employee
 * folders ("<name> - <last 4 SSN digits>") are checked against the vendor's
 * SSN file; SSN Updater files the linkable ones under their SSNs, the key an
 * upload job joins on.
 */
import type { MetaTone } from "../MetaPill";
import type {
  PdfJobPublishShort,
  PdfJobStatus,
  SplitSsnCheck,
  SplitSsnCheckSummary,
  SplitSsnUnlinkableReason,
} from "features/pdfTools/pdfTools";

export type SplitSsnState =
  | "hidden"
  | "not_saved"
  | "waiting"
  | "not_checked"
  | "checking"
  | "updater_running"
  | "error"
  | "ssn_file_missing"
  | "blocked"
  | "linkable"
  | "all_linked"
  | "none_linkable";

interface SsnFacts {
  status: SplitSsnCheck["status"];
  algorithm?: "ssn" | "names";
  folders: number;
  linkableExact: number;
  linkableSimilar: number;
  alreadyLinked: number;
  blocked: boolean;
  updaterRunning: boolean;
}

function factsOf(check: SplitSsnCheck | SplitSsnCheckSummary): SsnFacts {
  const updater = check.ssn_updater ?? null;
  const updaterRunning =
    updater != null && (updater.state ?? updater.status) === "running";
  if ("updater_estimate" in check || "s3_folders" in check) {
    const full = check as SplitSsnCheck;
    const estimate = full.updater_estimate ?? null;
    return {
      status: full.status,
      algorithm: full.algorithm,
      folders: Number(full.s3_folders ?? estimate?.folders ?? 0),
      linkableExact: Number(estimate?.linkable_exact ?? 0),
      linkableSimilar: Number(estimate?.linkable_similar ?? 0),
      alreadyLinked: Number(estimate?.already_linked ?? 0),
      blocked: Boolean(full.updater_blocked),
      updaterRunning,
    };
  }
  const summary = check as SplitSsnCheckSummary;
  return {
    status: summary.status,
    algorithm: summary.algorithm,
    folders: Number(summary.folders ?? 0),
    linkableExact: Number(summary.linkable_exact ?? 0),
    linkableSimilar: Number(summary.linkable_similar ?? 0),
    alreadyLinked: Number(summary.already_linked ?? 0),
    blocked: Boolean(summary.updater_blocked),
    updaterRunning,
  };
}

export function deriveSplitSsnState(job: {
  status: PdfJobStatus;
  publish: Pick<PdfJobPublishShort, "status">;
  discarded_at?: string | null;
  ssn_check: SplitSsnCheck | SplitSsnCheckSummary | null;
}): SplitSsnState {
  if (job.discarded_at || job.status !== "completed") return "hidden";
  if (job.publish.status === "pending" || job.publish.status === "running") return "waiting";
  if (job.publish.status !== "completed") return "not_saved";
  if (!job.ssn_check) return "not_checked";
  const facts = factsOf(job.ssn_check);
  if (facts.updaterRunning) return "updater_running";
  if (facts.status === "running") return "checking";
  if (facts.status === "error") return "error";
  if (facts.algorithm !== "ssn") return "ssn_file_missing";
  if (facts.linkableExact + facts.linkableSimilar > 0) {
    return facts.blocked ? "blocked" : "linkable";
  }
  return facts.alreadyLinked > 0 ? "all_linked" : "none_linkable";
}

export function splitSsnLinkable(check: SplitSsnCheck | SplitSsnCheckSummary | null): {
  exact: number;
  similar: number;
  total: number;
  folders: number;
  alreadyLinked: number;
} {
  if (!check) return { exact: 0, similar: 0, total: 0, folders: 0, alreadyLinked: 0 };
  const facts = factsOf(check);
  return {
    exact: facts.linkableExact,
    similar: facts.linkableSimilar,
    total: facts.linkableExact + facts.linkableSimilar,
    folders: facts.folders,
    alreadyLinked: facts.alreadyLinked,
  };
}

export const UNLINKABLE_LABELS: Record<SplitSsnUnlinkableReason, string> = {
  applied_for: "SSN applied for (no number on the form)",
  control_number: "named by an ADP control number",
  unknown_name: "name not read off the form",
  no_last4: "no SSN in the file ends with these digits",
  no_name_match: "the digits fit, the name does not",
  ambiguous: "several employees fit",
  no_separator: "not named <name> - <digits>",
  other_tail: "neither the last four digits nor an SSN",
};

/** Why folders cannot be linked, most frequent first. */
export function splitSsnReasons(check: SplitSsnCheck | null): { label: string; count: number }[] {
  const reasons = check?.unlinkable ?? {};
  return (Object.entries(reasons) as [SplitSsnUnlinkableReason, number][])
    .filter(([, value]) => Number(value) > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([reason, value]) => ({ label: UNLINKABLE_LABELS[reason] ?? reason, count: value }));
}

/** The tag a split job row carries for its SSN state; null when there is none. */
export function splitSsnListTag(
  state: SplitSsnState,
  check: SplitSsnCheck | SplitSsnCheckSummary | null,
): { label: string; tone: MetaTone } | null {
  switch (state) {
    case "linkable":
      return { label: `SSN: ${splitSsnLinkable(check).total} to link`, tone: "warning" };
    case "all_linked":
      return { label: "SSN linked", tone: "success" };
    case "none_linkable":
      return { label: "SSN: nothing to link", tone: "neutral" };
    case "ssn_file_missing":
      return { label: "No SSN file", tone: "warning" };
    case "blocked":
      return { label: "SSN: file in use", tone: "warning" };
    case "checking":
      return { label: "Checking SSN", tone: "info" };
    case "updater_running":
      return { label: "Linking SSNs", tone: "info" };
    case "error":
      return { label: "SSN check failed", tone: "error" };
    default:
      return null;
  }
}
